// КАП СВОБОДНЫХ ПОЛЕЙ T1–T3 НА СТОРОНЕ ВВОДА (`s824.d2`, шаг 3). B5-1.
//
// ⚠️ ЭТО ВТОРАЯ ПОЛОВИНА КАПА, А НЕ ВТОРОЙ КАП. Первая давно стоит на сервере:
// `api/extract-quotes.js` режет каждое поле `clampToBoundary`-ем по границе предложения, чтобы
// модель не цитировала из обрубка посреди слова. Здесь — ЖЁСТКИЙ СТОП ВВОДА: вставка и голосовая
// расшифровка обрезаются СРАЗУ, счётчика и предупреждающей строки нет, слот `s3.text.cap` не
// заводится (так сказано в решении дословно).
//
// ⛔ ПОЧЕМУ НЕ `clampToBoundary` ЗДЕСЬ. Границы у двух половин разные по назначению. Сервер режет
// ТЕКСТ, который человек уже отдал, и обязан отдать модели связный кусок. Поле режет ВВОД, и
// умная граница означала бы, что вставленный кусок молча теряет последнее предложение целиком —
// до полусотни символов, которые человек видел в буфере. Жёсткий рез оставляет ровно то, что
// влезло. `clampToBoundary` назван образцом ГРАНИЦ (`s824.d2`), а не реализацией.
//
// ⚠️ ЧИСЛО ОДНО НА ОБЕ ПОЛОВИНЫ, И ЭТО ПРОВЕРЯЕТСЯ. `MAX_INPUT_CHARS` живёт в серверной функции,
// импортировать её в браузерный модуль нельзя (за ней тянется sdk), поэтому здесь своя константа
// — а равенство двух держит утверждение в `deckField.test.js`. Разойдутся — покраснеет, и это
// дешевле, чем узнать о расхождении по обрезанной цитате.

import { LIVING_FOR_MAX } from '../../contracts.js';

/**
 * Потолок длины поля T1–T3 в символах (`s824.d2`). Равен `MAX_INPUT_CHARS` серверной функции.
 *
 * Считается в ЕДИНИЦАХ `String.length`, то есть в UTF-16, — ровно так же, как считает сервер.
 * Совпадение единиц важнее «правильности» счёта: разные единицы на двух концах означали бы, что
 * поле пропустило строку, которую сервер всё равно обрежет.
 */
export const FIELD_MAX = 5000;

/**
 * Обрезать значение поля до капа. Чистая функция: зовётся и на `paste`, и на приходе расшифровки,
 * и на любом другом пути, которым текст попадает в поле.
 *
 * Не-строка отдаётся пустой строкой, а не пропускается насквозь: поле держит `string`, и
 * `undefined` в нём доехал бы до конверта и до адаптера.
 */
export function clampField(value) {
  if (typeof value !== 'string') return '';
  return value.length <= FIELD_MAX ? value : value.slice(0, FIELD_MAX);
}

/**
 * Обрезать СВОБОДНУЮ СТРОКУ BG3 до `LIVING_FOR_MAX` (`s510.d3`, кап 64). B5-4.
 *
 * ⚠️ ЭТО ВТОРОЙ ПОЯС, А НЕ ЗАМЕНА `maxlength`. Атрибут на поле обязан стоять — он и есть то,
 * ради чего заводилось решение: человек узнаёт о пределе НА ВВОДЕ, а не из обрезки в готовом
 * паспорте. Но атрибут держит только человека: программная запись значения его не видит вовсе
 * (замерено в браузере — 68 символов доехали до конверта при `maxlength="64"`), а такой путь у
 * поля уже запланирован — расшифровка голоса в блоке 5г пишет в него не пальцами.
 *
 * ⛔ КАП ЖЁСТКИЙ, БЕЗ УМНОЙ ГРАНИЦЫ. Обрезка по границе слова живёт в рендере (`SectionII`,
 * `CAP_LIVING`) и решает другую задачу — печатать связный кусок. Здесь режется ВВОД, и умная
 * граница молча съела бы последнее слово целиком. Та же развилка, что у `clampField`.
 *
 * Число берётся из контракта: второй копии `64` в визарде нет (`s524.d2`).
 */
export function clampLivingFor(value) {
  if (typeof value !== 'string') return '';
  return value.length <= LIVING_FOR_MAX ? value : value.slice(0, LIVING_FOR_MAX);
}

/**
 * Сколько символов ещё влезет. Нужна НЕ для счётчика на экране (его нет по решению), а для
 * обработчика ввода: он обязан знать, сколько взять из вставки, а не обрезать поле целиком после.
 */
export function roomLeft(value) {
  return Math.max(0, FIELD_MAX - (typeof value === 'string' ? value.length : 0));
}

/**
 * Вставка в позицию: что станет значением поля, если в `value` на отрезок `[start, end)` вставить
 * `insert`. Обрезается ВСТАВКА, а не результат.
 *
 * ⚠️ РАЗНИЦА НЕ КОСМЕТИЧЕСКАЯ. Обрежь результат — и вставка в СЕРЕДИНУ полного поля съела бы
 * хвост, который человек написал сам: текст после курсора уехал бы за кап и пропал. Обрезанная
 * вставка теряет только то, что человек и так не мог добавить.
 */
export function pasteInto(value, insert, start, end = start) {
  const text = typeof value === 'string' ? value : '';
  const from = Math.max(0, Math.min(start ?? text.length, text.length));
  const to = Math.max(from, Math.min(end ?? from, text.length));
  const head = text.slice(0, from);
  const tail = text.slice(to);
  const room = Math.max(0, FIELD_MAX - head.length - tail.length);
  const chunk = (typeof insert === 'string' ? insert : '').slice(0, room);
  return `${head}${chunk}${tail}`;
}
